import { useEffect, useState } from 'react';
import { Col, Row } from 'reactstrap';
import BreadCrumb from '../../../Components/Common/BreadCrumb';
import { NavLink, useNavigate } from 'react-router-dom';
import {
  getPublishedPromotions,
  updatePromotionPriority,
} from '../../../actions/promotion/index';
import InnerLoader from '../../../Components/Common/InnerLoader';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import { If, Then } from 'react-if';
import { DISCARD_SUBSEQUENTLY_OPTIONS_OBJ } from '../PromotionCoupons';
import { useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';

const PromotionPrioritization = () => {
  const navigate = useNavigate();
  const { user } = useSelector((state) => state?.user || {});

  const [promotions, setPromotions] = useState([]);
  const [totalPages, setTotalPages] = useState(1);
  const [isLoading, setIsLoading] = useState(true);
  const { campaignTypeId } = useParams();

  useEffect(() => {
    getPublishedPromotionsFunc();
  }, []);

  const getPublishedPromotionsFunc = async () => {
    setIsLoading(true);
    const inputData = {
      pageSize: 20,
      pageOffset: 0,
      filters: {
        campaignType: campaignTypeId?.toUpperCase()?.replaceAll('-', '_'),
      },
    };

    const { success, data } = await getPublishedPromotions(inputData);
    if (success) {
      setPromotions(data?.promotions);
      setTotalPages(data?.totalPages);
      setTimeout(() => {
        setIsLoading(false);
      }, 400);
    }
  };

  const onDragEnd = async (result) => {
    try {
      if (!result?.destination) {
        return;
      }

      // Reorder the promotions array
      const reorderedPromotions = [...promotions];
      const [removed] = reorderedPromotions?.splice(result?.source?.index, 1);
      reorderedPromotions?.splice(result?.destination?.index, 0, removed);

      setPromotions([...reorderedPromotions]);

      // Recalculate the priorities
      const updatedPromotionOrder = await Promise?.all(
        reorderedPromotions.map((promo, index) => ({
          promotionId: promo?._id,
          priority: index + 1, // Assign new priority based on new position
        }))
      );

      await updatePromotionPriority({
        priorityList: updatedPromotionOrder,
      });
    } catch (error) {
      console.log(error);
    }
  };

  document.title = 'Promotion Prioritization';
  return (
    <div className="page-content px-4 pb-0">
      <BreadCrumb
        titleInActive="Promotions"
        titleActive="Prioritization Order"
      />
      <Row>
        <Col md={12} className="m-auto">
          <div
            style={{ backdropFilter: ' blur(4px)' }}
            className="position-sticky top-0 z-1 d-flex justify-content-between align-items-center py-4"
          >
            <h4 className="mb-0">Prioritization Order</h4>
          </div>

          <DragDropContext onDragEnd={onDragEnd}>
            <Droppable droppableId="droppable" type="priority">
              {(provided, snapshot) => (
                <div
                  {...provided?.droppableProps}
                  ref={provided?.innerRef}
                  className="mb-5 position-relative d-flex flex-column"
                >
                  <table className="table table-nowrap table-centered align-middle mb-0">
                    <thead className="bg-light fs-14">
                      <tr>
                        <th></th>
                        <th></th>
                        <th className="w-70">Promotion Names</th>
                        <th style={{ width: 150 }}></th>
                        <th>Discard Settings</th>
                      </tr>
                    </thead>
                    {promotions?.map((promotion, index) => {
                      return (
                        <Draggable
                          key={promotion?._id?.toString()}
                          draggableId={promotion?._id?.toString()}
                          index={index}
                          isDragDisabled={
                            Boolean(user?.dashboardSettings?.countryCode) ||
                            Boolean(user?.dashboardSettings?.storeId)
                          }
                        >
                          {(provided, snapshot) => (
                            <tr
                              ref={provided?.innerRef}
                              {...provided?.draggableProps}
                              {...provided?.dragHandleProps}
                            >
                              <td
                                className="fs-13 text-muted text-center"
                                style={{ width: 50 }}
                              >
                                {index + 1}
                              </td>
                              <td style={{ width: 50 }}>
                                <i className="bx bx-grid-vertical ms-2 me-2 fs-18"></i>
                              </td>
                              <td className="text-capitalize fs-14 text-muted">
                                <NavLink
                                  to={`/promotions/${campaignTypeId}/detail/${promotion?._id}/basic`}
                                  className="fw-medium text-primary"
                                >
                                  {promotion?.basicSettings?.promotionName}
                                </NavLink>
                              </td>
                              <td>
                                <If
                                  condition={
                                    promotion?.basicSettings?.alwaysRun
                                  }
                                >
                                  <Then>
                                    <p className="m-0 fs-11 text-muted">
                                      ALWAYS RUN ✅{' '}
                                    </p>
                                  </Then>
                                </If>
                              </td>
                              <td>
                                {DISCARD_SUBSEQUENTLY_OPTIONS_OBJ[
                                  promotion?.basicSettings?.discard
                                ]?.label && (
                                  <>
                                    <p className="m-0 fs-11 text-muted">
                                      <strong className="text-dark">
                                        {
                                          DISCARD_SUBSEQUENTLY_OPTIONS_OBJ[
                                            promotion.basicSettings.discard
                                          ]?.label
                                        }
                                      </strong>
                                    </p>
                                  </>
                                )}
                              </td>
                            </tr>
                          )}
                        </Draggable>
                      );
                    })}
                  </table>
                </div>
              )}
            </Droppable>
          </DragDropContext>
        </Col>
      </Row>
      {isLoading ? <InnerLoader /> : null}
    </div>
  );
};

export default PromotionPrioritization;
